/**
 * The calculator's four questions — one per screen, each checking only its own
 * answers before the next (`useCalculatorWizard`). The choices are cards, not
 * a list of radio dots; a date is picked on a calendar in the app's language.
 */
import { addMonths, dateOf, dayOf, inoutTouchingAbsence, isCountryNotServed } from '@tonnie/heroes';
import { NEXT_DESTINATIONS, OPEN_DESTINATIONS } from '@tonnie/foundation';
import { ChevronDown, Edit2, Plus, Trash2 } from '@tonnie/assets';
import { useCountryName, useLocale } from '@tonnie/locale';
import { inoutAbsenceFormSchema, type InOutAbsenceFormInput } from '@tonnie/schemas';
import {
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  CountryFlag,
  Label,
  RadioGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  describedBy,
  FieldError,
  SectionErrorState,
  DateField,
  RadioCard,
  cn,
} from '@tonnie/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InOutTripFields } from '../forms/InOutTripFields';

import type { CalculatorWizard } from '../../hooks/use-calculator-wizard';
import { CALENDAR_YEARS, deviceToday, deviceYear } from '../../lib/local-today';
import type { TripRow } from '../../lib/kept-calculation';
import { INOUT_TONE, INOUT_CLASSES } from '../../lib/inout-tone';

/** How many titles show before "show all" — the common ones come first in the config. */
const TITLES_SHOWN_FIRST = 3;

function Continue({
  onClick,
  busy = false,
  label,
  testId = 'inout-calculator-continue',
}: {
  onClick: () => void;
  busy?: boolean;
  label: string;
  testId?: string;
}) {
  return (
    <Button
      type="button"
      onClick={onClick}
      loading={busy}
      tone={INOUT_TONE}
      className="h-11 w-full"
      data-testid={testId}
    >
      {label}
    </Button>
  );
}

// ─── 1. The host country ─────────────────────────────────────────────────────

export function CountryStep({ wizard }: { wizard: CalculatorWizard }) {
  const { t } = useLocale();
  const countryName = useCountryName();
  const [tried, setTried] = useState(false);
  const { country, regimes } = wizard;
  const notServed = !!country && regimes.isError && isCountryNotServed(regimes.error);

  return (
    <div className="flex flex-col gap-5">
      <RadioGroup
        value={country}
        onValueChange={wizard.setCountry}
        className="flex flex-col gap-3"
        aria-label={t('inout.calculator.country.question')}
        aria-describedby={describedBy(tried && !country && 'inout-calculator-country-error')}
      >
        {OPEN_DESTINATIONS.map((code) => (
          <RadioCard
            tone={INOUT_TONE}
            key={code}
            id={`inout-calculator-country-${code}`}
            value={code}
            selected={country === code}
            data-testid={`inout-calculator-country-${code}`}
          >
            <span className="flex items-center gap-3">
              <CountryFlag code={code} size="md" />
              <span className="text-base font-semibold text-foreground">{countryName(code)}</span>
            </span>
          </RadioCard>
        ))}
      </RadioGroup>

      {NEXT_DESTINATIONS.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-muted-foreground">
            {t('inout.calculator.country.comingSoon')}
          </p>
          <ul className="flex flex-wrap gap-2">
            {NEXT_DESTINATIONS.map((code) => (
              <li
                key={code}
                className="flex items-center gap-2 rounded-pill border border-border px-3 py-1.5 text-sm text-foreground"
              >
                <CountryFlag code={code} size="sm" />
                {countryName(code)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {notServed && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('inout.setup.notServed', { country: countryName(country) })}
        </p>
      )}
      <FieldError id="inout-calculator-country-error">
        {tried && !country ? t('inout.calculator.country.required') : null}
      </FieldError>
      <Continue
        label={t('common.continue')}
        onClick={() => {
          setTried(true);
          if (!notServed) void wizard.next();
        }}
      />
    </div>
  );
}

// ─── 2. The title ────────────────────────────────────────────────────────────

export function TitleStep({ wizard }: { wizard: CalculatorWizard }) {
  const { t, formatNumber } = useLocale();
  const { form, regimes } = wizard;
  const [showAll, setShowAll] = useState(false);
  const chosenId = form.watch('regime');
  const options = regimes.data?.regimes ?? [];
  const chosenIndex = options.findIndex((r) => r.id === chosenId);
  // A choice past the first ones keeps the whole list open.
  const visible =
    showAll || chosenIndex >= TITLES_SHOWN_FIRST ? options : options.slice(0, TITLES_SHOWN_FIRST);
  const hidden = options.length - visible.length;
  const chosen = chosenIndex >= 0 ? options[chosenIndex] : null;
  const regimeError = form.formState.errors.regime;

  if (regimes.isLoading) return <Skeleton className="h-64 w-full" />;
  if (regimes.isError || !regimes.data) {
    return (
      <SectionErrorState
        size="inline"
        title={t('inout.setup.regimesError')}
        onRetry={() => void regimes.refetch()}
        retrying={regimes.isFetching}
        retryLabel={t('common.retry')}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Controller
        control={form.control}
        name="regime"
        render={({ field }) => (
          <RadioGroup
            value={field.value}
            onValueChange={(value) => {
              field.onChange(value);
              // A subtype belongs to its regime.
              form.setValue('subtype', '');
              wizard.onTitleChanged();
            }}
            className="flex flex-col gap-3"
            aria-label={t('inout.setup.regime')}
            aria-describedby={describedBy(regimeError && 'inout-calculator-regime-error')}
          >
            {visible.map((regime) => (
              <RadioCard
                tone={INOUT_TONE}
                key={regime.id}
                id={`inout-calculator-regime-${regime.id}`}
                value={regime.id}
                selected={field.value === regime.id}
                data-testid={`inout-calculator-regime-${regime.id}`}
              >
                <span className="block text-base font-semibold text-foreground">
                  {regime.label}
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">{regime.summary}</span>
              </RadioCard>
            ))}
          </RadioGroup>
        )}
      />
      {hidden > 0 && (
        <Button
          type="button"
          variant="ghost"
          className={cn('self-start', INOUT_CLASSES.text)}
          onClick={() => setShowAll(true)}
          data-testid="inout-calculator-regime-show-all"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('inout.calculator.title.showAll', { count: hidden, titles: formatNumber(hidden) })}
        </Button>
      )}

      <Collapsible className="self-start" data-testid="inout-calculator-regime-not-sure">
        <CollapsibleTrigger asChild>
          <Button type="button" variant="ghost" className={cn('group gap-1.5', INOUT_CLASSES.text)}>
            {t('inout.calculator.title.notSure')}
            <ChevronDown
              className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pt-1 text-sm text-muted-foreground">
          {t('inout.calculator.title.notSureHelp')}
        </CollapsibleContent>
      </Collapsible>

      {chosen && chosen.subtypes.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="inout-calculator-subtype">{t('inout.setup.subtype')}</Label>
          <Controller
            control={form.control}
            name="subtype"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  wizard.onTitleChanged();
                }}
              >
                <SelectTrigger id="inout-calculator-subtype" className="h-11 w-full">
                  <SelectValue placeholder={t('inout.calculator.title.subtypeNone')} />
                </SelectTrigger>
                <SelectContent>
                  {chosen.subtypes.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      <FieldError id="inout-calculator-regime-error">
        {regimeError ? t('inout.setup.regimeRequired') : null}
      </FieldError>
      <Continue label={t('common.continue')} onClick={() => void wizard.next()} />
    </div>
  );
}

// ─── 3. The dates ────────────────────────────────────────────────────────────

export function DatesStep({ wizard }: { wizard: CalculatorWizard }) {
  const { t, intlLocale } = useLocale();
  const { form, regimes } = wizard;
  const regime = regimes.data?.regimes.find((r) => r.id === form.watch('regime')) ?? null;
  // A status with no expiry starts with "no end date" ticked; a person may untick it.
  const [noEnd, setNoEnd] = useState(
    regime?.validityMonths === null && !form.getValues('validUntil')
  );
  const today = deviceToday();
  const year = deviceYear();
  const { errors } = form.formState;
  const issuedOn = form.watch('issuedOn');
  // The expiry's calendar opens on the title's usual validity from its issue.
  const usualExpiry =
    issuedOn && regime?.validityMonths
      ? dateOf(addMonths(dayOf(issuedOn), regime.validityMonths))
      : undefined;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="inout-calculator-issued">{t('inout.setup.issuedOn')}</Label>
        <Controller
          control={form.control}
          name="issuedOn"
          render={({ field }) => (
            <DateField
              id="inout-calculator-issued"
              value={field.value}
              onChange={(iso) => {
                field.onChange(iso);
                wizard.onTitleChanged();
              }}
              intlLocale={intlLocale}
              calendarLabel={t('inout.calculator.dates.issuedPlaceholder')}
              clearLabel={t('common.clear')}
              max={today}
              fromYear={year - CALENDAR_YEARS}
              toYear={year}
              invalid={!!errors.issuedOn}
              describedBy={describedBy(errors.issuedOn && 'inout-calculator-issued-error')}
            />
          )}
        />
        <FieldError id="inout-calculator-issued-error">
          {errors.issuedOn ? t('inout.form.dateInvalid') : null}
        </FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="inout-calculator-valid">{t('inout.setup.validUntil')}</Label>
        {noEnd ? (
          <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground">
            {t('inout.calculator.dates.noEndSet')}
          </p>
        ) : (
          <Controller
            control={form.control}
            name="validUntil"
            render={({ field }) => (
              <DateField
                id="inout-calculator-valid"
                value={field.value}
                onChange={(iso) => {
                  field.onChange(iso);
                  wizard.onTitleChanged();
                }}
                intlLocale={intlLocale}
                calendarLabel={t('inout.calculator.dates.validPlaceholder')}
                clearLabel={t('common.clear')}
                calendarMin={issuedOn || undefined}
                openOn={usualExpiry}
                fromYear={year - CALENDAR_YEARS}
                toYear={year + CALENDAR_YEARS}
                invalid={!!errors.validUntil}
                describedBy={describedBy(errors.validUntil && 'inout-calculator-valid-error')}
              />
            )}
          />
        )}
        <FieldError id="inout-calculator-valid-error">
          {errors.validUntil ? t('inout.setup.expiresBeforeIssued') : null}
        </FieldError>
        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id="inout-calculator-no-end"
            checked={noEnd}
            onCheckedChange={(checked) => {
              const on = checked === true;
              setNoEnd(on);
              if (on) form.setValue('validUntil', '');
              wizard.onTitleChanged();
            }}
          />
          <Label htmlFor="inout-calculator-no-end" className="font-normal">
            {t('inout.calculator.dates.noEnd')}
          </Label>
        </div>
      </div>

      <Continue label={t('common.continue')} onClick={() => void wizard.next()} />
    </div>
  );
}

// ─── 4. The trips ────────────────────────────────────────────────────────────

const EMPTY_TRIP: InOutAbsenceFormInput = {
  departedOn: '',
  returnedOn: '',
  destinationCountry: '',
};

/**
 * One trip, written or corrected — dates on a calendar, the destination if
 * known. One that shares only a border day with another of the list (out and
 * back twice the same day) is offered to be joined into it — the day counts
 * once (owner, 22 Oct 2026); any other overlap is the calculation's to refuse.
 */
function TripEditor({
  initial,
  others,
  onSave,
  onJoin,
  onCancel,
}: {
  initial: InOutAbsenceFormInput;
  /** The list's other trips — what this one may touch. */
  others: readonly TripRow[];
  onSave: (trip: InOutAbsenceFormInput) => void;
  /** Join it to the trip it touches. */
  onJoin: (trip: InOutAbsenceFormInput, touched: TripRow) => void;
  onCancel: () => void;
}) {
  const { t, formatDate } = useLocale();
  const { control, handleSubmit } = useForm<InOutAbsenceFormInput>({
    resolver: zodResolver(inoutAbsenceFormSchema),
    defaultValues: initial,
  });
  const [touching, setTouching] = useState<{
    trip: InOutAbsenceFormInput;
    touched: TripRow;
  } | null>(null);
  const day = (iso: string) => formatDate(iso, 'calendarDay');

  const save = (values: InOutAbsenceFormInput) => {
    const touched = inoutTouchingAbsence(
      { departedOn: values.departedOn, returnedOn: values.returnedOn || null },
      others
    );
    if (touched) setTouching({ trip: values, touched });
    else onSave(values);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-xl border p-4',
        INOUT_CLASSES.line,
        INOUT_CLASSES.subtle
      )}
      data-testid="inout-calculator-trip-editor"
    >
      <InOutTripFields
        control={control}
        names={{ from: 'departedOn', to: 'returnedOn', destination: 'destinationCountry' }}
        idPrefix="inout-calculator"
        when="past"
      />
      {touching && (
        <div className="flex flex-col gap-2" data-testid="inout-calculator-trip-touches">
          <p role="alert" className="text-sm text-foreground">
            {touching.touched.returnedOn
              ? t('inout.errors.touches', {
                  from: day(touching.touched.departedOn),
                  to: day(touching.touched.returnedOn),
                })
              : t('inout.errors.touchesOpen', { from: day(touching.touched.departedOn) })}
          </p>
          <Button
            type="button"
            variant="outline"
            className="self-start"
            onClick={() => onJoin(touching.trip, touching.touched)}
            data-testid="inout-calculator-trip-join"
          >
            {t('inout.absence.join')}
          </Button>
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleSubmit(save)}
          tone={INOUT_TONE}
          data-testid="inout-calculator-trip-save"
        >
          {t('inout.calculator.trips.save')}
        </Button>
      </div>
    </div>
  );
}

const toRow = (key: string, trip: InOutAbsenceFormInput): TripRow => ({
  key,
  departedOn: trip.departedOn,
  returnedOn: trip.returnedOn || null,
  destinationCountry: trip.destinationCountry || null,
});

export function TripsStep({ wizard }: { wizard: CalculatorWizard }) {
  const { t, formatDate } = useLocale();
  const countryName = useCountryName();
  // The editor: closed, open for a new trip, or open on one of the list's.
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const host = countryName(wizard.country);

  return (
    <div className="flex flex-col gap-5">
      {wizard.trips.length > 0 && (
        <ul className="flex flex-col gap-3" data-testid="inout-calculator-trips">
          {wizard.trips.map((trip) =>
            editing === trip.key ? (
              <li key={trip.key}>
                <TripEditor
                  initial={{
                    departedOn: trip.departedOn,
                    returnedOn: trip.returnedOn ?? '',
                    destinationCountry: trip.destinationCountry ?? '',
                  }}
                  others={wizard.trips.filter((other) => other.key !== trip.key)}
                  onSave={(values) => {
                    wizard.updateTrip(toRow(trip.key, values));
                    setEditing(null);
                  }}
                  onJoin={(values, touched) => {
                    wizard.joinTrips(toRow(trip.key, values), touched.key);
                    setEditing(null);
                  }}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li
                key={trip.key}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5"
                data-testid="inout-calculator-trip"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {trip.returnedOn
                      ? t('inout.absence.range', {
                          from: formatDate(trip.departedOn, 'calendarDay'),
                          to: formatDate(trip.returnedOn, 'calendarDay'),
                        })
                      : t('inout.absence.stillAway', {
                          from: formatDate(trip.departedOn, 'calendarDay'),
                        })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {trip.destinationCountry
                      ? countryName(trip.destinationCountry)
                      : t('inout.absence.noDestination')}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditing(trip.key)}
                  aria-label={t('inout.absence.edit', {
                    from: formatDate(trip.departedOn, 'calendarDay'),
                  })}
                >
                  <Edit2 className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => wizard.removeTrip(trip.key)}
                  aria-label={t('inout.absence.remove', {
                    from: formatDate(trip.departedOn, 'calendarDay'),
                  })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </li>
            )
          )}
        </ul>
      )}

      {editing === 'new' ? (
        <TripEditor
          initial={EMPTY_TRIP}
          others={wizard.trips}
          onSave={(values) => {
            wizard.addTrip(toRow(crypto.randomUUID(), values));
            setEditing(null);
          }}
          onJoin={(values, touched) => {
            wizard.joinTrips(toRow(crypto.randomUUID(), values), touched.key);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <Button
          type="button"
          variant="outline"
          className={cn('h-11 justify-start gap-2 border-dashed', INOUT_CLASSES.text)}
          onClick={() => setEditing('new')}
          data-testid="inout-calculator-add-trip"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('inout.calculator.trips.add')}
        </Button>
      )}

      {wizard.trips.length === 0 && editing !== 'new' && (
        <p className="text-sm text-muted-foreground">{t('inout.calculator.trips.none')}</p>
      )}

      <div className="flex flex-col gap-1 rounded-xl bg-muted/50 p-4">
        <div className="flex items-start gap-2">
          <Checkbox
            id="inout-calculator-rest-in-host"
            checked={wizard.restInHost}
            onCheckedChange={(checked) => wizard.setRestInHost(checked === true)}
            className="mt-0.5"
          />
          <Label htmlFor="inout-calculator-rest-in-host" className="leading-snug">
            {t('inout.presence.restInHost', { country: host })}
          </Label>
        </div>
        <p className="pl-6 text-xs text-muted-foreground">{t('inout.presence.restInHostHint')}</p>
      </div>

      {wizard.problem && (
        <p role="alert" className="text-sm text-destructive-accessible">
          {wizard.problem}
        </p>
      )}
      {/* A trip written but not saved would be lost in silence: the answer waits for it. */}
      {editing !== null && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('inout.calculator.trips.saveFirst')}
        </p>
      )}
      <Continue
        label={t('inout.calculator.trips.calculate')}
        busy={wizard.calculating || editing !== null}
        onClick={() => void wizard.next()}
        testId="inout-calculator-calculate"
      />
    </div>
  );
}
